import { z } from 'zod';
import { uuidSchema } from './common.schema';

/**
 * Zod schema for the POST /audit request body.
 * Frontend calls this endpoint explicitly to log audit events.
 */
export const AuditLogRequestSchema = z.object({
  eventCategory: z.string().min(1).max(50),
  eventType: z.string().min(1).max(100),
  eventStatus: z.enum(['SUCCESS', 'FAILURE', 'PARTIAL']),
  resourceType: z.string().max(100).optional(),
  resourceId: uuidSchema.optional(),
  oldValue: z.unknown().optional(),
  newValue: z.unknown().optional(),
  errorCode: z.string().max(50).optional(),
  errorMessage: z.string().optional(),
  httpMethod: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']).optional(),
  apiPath: z.string().max(300).optional(),
  apiVersion: z.string().max(10).optional(),
  responseStatus: z.number().int().min(100).max(599).optional(),
  durationMs: z.number().int().nonnegative().optional(),
  response: z.unknown().optional(),
  isOperational: z.boolean().optional(),
  isPhiAccess: z.boolean().optional(),
  // Optional identity override — populated by the frontend when no valid token
  // is available (e.g. logout audit log sent after the session is invalidated).
  userId:     uuidSchema.optional(),
  hospitalId: uuidSchema.optional(),
  sessionId:  uuidSchema.optional(),
  scopeType:  z.string().max(50).optional(),
  email:      z.string().email().max(254).optional(),
});

export type AuditLogRequest = z.infer<typeof AuditLogRequestSchema>;

export const AuditLogResponseSchema = z.object({
  success: z.literal(true),
  message: z.string(),
});

/**
 * Query params for GET /audit and GET /audit/decrypt
 */
export const AuditLogQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  eventCategory: z.string().max(50).optional(),
  eventType: z.string().max(100).optional(),
  eventStatus: z.enum(['SUCCESS', 'FAILURE', 'PARTIAL']).optional(),
  resourceType: z.string().max(100).optional(),
  resourceId: uuidSchema.optional(),
  userId: uuidSchema.optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
});

export type AuditLogQuery = z.infer<typeof AuditLogQuerySchema>;

/**
 * Single audit log row (simple view — sensitive fields omitted)
 */
export const AuditLogItemSchema = z.object({
  auditId: uuidSchema,
  tenantId: uuidSchema,
  userId: uuidSchema.nullable(),
  sessionId: uuidSchema.nullable(),
  eventTime: z.string(),
  eventCategory: z.string(),
  eventType: z.string(),
  eventStatus: z.string(),
  resourceType: z.string().nullable(),
  resourceId: uuidSchema.nullable(),
  scopeType: z.string().nullable(),
  scopeId: uuidSchema.nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  correlationId: uuidSchema.nullable(),
  errorCode: z.string().nullable(),
  isPhiAccess: z.boolean(),
  username: z.string().nullable(),
  email: z.string().nullable(),
});

/**
 * Decrypted audit log row — includes sensitive fields
 */
export const AuditLogDecryptedItemSchema = AuditLogItemSchema.extend({
  oldValue: z.unknown().nullable(),
  newValue: z.unknown().nullable(),
  errorMessage: z.string().nullable(),
});

export const AuditLogListResponseSchema = z.object({
  data: z.array(AuditLogItemSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
});

export const AuditLogDecryptedListResponseSchema = z.object({
  data: z.array(AuditLogDecryptedItemSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
});

/**
 * Onboarding audit log item — control-plane scope, no tenantId / scopeId.
 */
export const OnboardingAuditLogItemSchema = z.object({
  auditId: uuidSchema,
  userId: uuidSchema.nullable(),
  username: z.string().nullable(),
  email: z.string().nullable(),
  sessionId: uuidSchema.nullable(),
  eventTime: z.string(),
  eventCategory: z.string(),
  eventType: z.string(),
  eventStatus: z.string(),
  resourceType: z.string().nullable(),
  resourceId: uuidSchema.nullable(),
  scopeType: z.string().nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  correlationId: uuidSchema.nullable(),
  errorCode: z.string().nullable(),
  isPhiAccess: z.boolean(),
});

export type OnboardingAuditLogItem = z.infer<typeof OnboardingAuditLogItemSchema>;

export const OnboardingAuditLogDecryptedItemSchema = OnboardingAuditLogItemSchema.extend({
  oldValue: z.unknown().nullable(),
  newValue: z.unknown().nullable(),
  errorMessage: z.string().nullable(),
});

export type OnboardingAuditLogDecryptedItem = z.infer<typeof OnboardingAuditLogDecryptedItemSchema>;

export const OnboardingAuditLogListResponseSchema = z.object({
  data: z.array(OnboardingAuditLogItemSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
});

export const OnboardingAuditLogDecryptedListResponseSchema = z.object({
  data: z.array(OnboardingAuditLogDecryptedItemSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
});
